import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiUrl from '@/apis/api';
import { ProviderResource } from '@/lib/auth/providers';

/**
 * Utility hook to invalidate self-hosted manager orgs cache
 * Use this after creating/updating a self-hosted provider
 */
export const useInvalidateSelfHostedManagerOrgs = () => {
  const queryClient = useQueryClient();

  return () => {
    // Invalidate React Query cache
    queryClient.invalidateQueries({ queryKey: ['selfHostedManagerOrgs'] });

    // Clear server-side cache via API route
    fetch('/api/self-hosted-manager/clear-cache', { method: 'POST' }).catch(
      (err) => console.error('Failed to clear server cache:', err)
    );
  };
};

/**
 * Hook to fetch self-hosted manager organizations with React Query caching
 * Prevents duplicate calls when multiple components need the same data
 *
 * Note: This hook uses the non-sensitive endpoint (fetchAllSelfHostedManagerOrgs)
 * which doesn't return client_secret. The sensitive endpoint is only used
 * server-side in NextAuth for OAuth configuration.
 *
 * Self-hosted org configs rarely change, so we use aggressive caching:
 * - staleTime: 1 hour (data is considered fresh for 1 hour)
 * - gcTime: 2 hours (keeps in cache for 2 hours after last use)
 *
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with provider resources
 */
export const useSelfHostedManagerOrgs = (enabled: boolean = true) => {
  return useQuery<ProviderResource[], Error>({
    queryKey: ['selfHostedManagerOrgs'],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}fetchAllSelfHostedManagerOrgs/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to fetch providers: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`
        );
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error(
          'Invalid response format: expected an array of providers'
        );
      }

      return data.map(
        (provider: any) =>
          ({
            id: provider.organization_name,
            name: `self-hosted-${provider.instance_url}`,
            baseUrl: provider.instance_url,
            isSelfHosted: true,
          }) as ProviderResource
      );
    },
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour - self-hosted org configs rarely change
    gcTime: 1000 * 60 * 60 * 2, // 2 hours - keep in cache longer
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount if data exists and is fresh
  });
};
